// src/screens/BlockTemplates.tsx
import { useEffect, useMemo, useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  NotebookPen,
  Filter,
  Search,
  Copy,
  Trash2,
  Sparkles,
  Timer,
  Layers3,
  Library,
} from "lucide-react";
import { TrainingDrawingCanvas } from "../components/TrainingDrawingCanvas";

/* -------------------- Types -------------------- */
type BlockKind = "echauffement" | "exercice" | "prepa";
type CourtType = "half" | "full";

type DrawElementKind = "arrow" | "cone" | "player" | "text" | "pass";
type DrawElement = {
  id: string;
  kind: DrawElementKind;
  x: number;
  y: number; // 0..1
  x2?: number;
  y2?: number; // arrow/pass
  label?: string;
  color?: string;
  cpx?: number;
  cpy?: number; // courbe quad flèche
  // hasBall éventuellement présent (PlanTraining)
  hasBall?: boolean;
};

type DrawingV2 = {
  id: string;
  type: CourtType;
  paths: { x: number; y: number }[][];
  elements?: DrawElement[]; // 🟢 outils
};

type ExerciseBlock = {
  id: string;
  kind: BlockKind;
  name: string;
  title?: string;
  duration: string;
  objectives: string[];
  description: string;
  technicalNotes: string;
  evolutions: string;
  drawings: DrawingV2[];
};

type BlockTemplateRow = {
  id: string;
  user_id: string;
  name: string;
  kind: BlockKind;
  payload: ExerciseBlock;
  created_at: string;
};

const KIND_LABEL: Record<BlockKind, string> = {
  echauffement: "Échauffement",
  exercice: "Exercice",
  prepa: "Prépa",
};

/* ------------ petites helpers responsive comme TrainingDetails ------------ */

// Clamp général
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

// Nettoie les paths (évite NaN)
function sanitizePaths(paths?: { x: number; y: number }[][]): { x: number; y: number }[][] {
  if (!Array.isArray(paths)) return [];
  return paths
    .map((seg) =>
      Array.isArray(seg)
        ? seg.filter((pt) => pt && Number.isFinite((pt as any).x) && Number.isFinite((pt as any).y))
        : []
    )
    .filter((seg) => seg.length > 0);
}

// Détection & normalisation des anciens dessins (coords en pixels)
function normalizeLegacyDrawing(d: DrawingV2): DrawingV2 {
  if (!d) return d;

  const paths = Array.isArray(d.paths) ? d.paths : [];
  const elements = Array.isArray(d.elements) ? d.elements : [];

  const hasBigCoordInPaths = paths.some((seg) => seg.some((pt) => pt.x > 1.5 || pt.y > 1.5));
  const hasBigCoordInElems = elements.some(
    (el) =>
      el.x > 1.5 ||
      el.y > 1.5 ||
      (el.x2 ?? 0) > 1.5 ||
      (el.y2 ?? 0) > 1.5 ||
      (el.cpx ?? 0) > 1.5 ||
      (el.cpy ?? 0) > 1.5
  );

  if (!hasBigCoordInPaths && !hasBigCoordInElems) {
    return d; // déjà normalisé
  }

  const BASE_W = 520;
  const BASE_H_FULL = 520;
  const BASE_H_HALF = 520 * 1.3;
  const baseH = d.type === "half" ? BASE_H_HALF : BASE_H_FULL;

  const norm = (v: number, max: number) => v / max;

  const normPaths = hasBigCoordInPaths
    ? paths.map((seg) =>
        seg.map((pt) => ({
          x: norm(pt.x, BASE_W),
          y: norm(pt.y, baseH),
        }))
      )
    : paths;

  const normElems = hasBigCoordInElems
    ? elements.map((el) => ({
        ...el,
        x: norm(el.x, BASE_W),
        y: norm(el.y, baseH),
        ...(el.x2 != null ? { x2: norm(el.x2, BASE_W) } : {}),
        ...(el.y2 != null ? { y2: norm(el.y2, baseH) } : {}),
        ...(el.cpx != null ? { cpx: norm(el.cpx, BASE_W) } : {}),
        ...(el.cpy != null ? { cpy: norm(el.cpy, baseH) } : {}),
      }))
    : elements;

  return {
    ...d,
    paths: normPaths,
    elements: normElems,
  };
}

// hook pour mesurer largeur container
function useContainerWidth<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const [w, setW] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (e.contentRect?.width != null) {
          setW(e.contentRect.width);
        }
      }
    });
    obs.observe(el);
    setW(el.getBoundingClientRect().width);
    return () => obs.disconnect();
  }, []);

  return { ref, width: w };
}

/* ------------ Overlay read-only (SVG) qui scale et clippe ------------ */
const ElementsOverlayRO: React.FC<{
  elements: DrawElement[];
  width: number;
  height: number;
  iconScale?: number;
}> = ({ elements, width, height, iconScale = 1 }) => {
  const basePlayerR = 16;
  const r = basePlayerR * iconScale;
  const font = Math.max(10, Math.round(14 * iconScale));

  return (
    <svg className="pointer-events-none absolute inset-0" width={width} height={height}>
      {elements.map((el) => {
        const nx = clamp01(el.x);
        const ny = clamp01(el.y);
        const x = nx * width;
        const y = ny * height;

        if (el.kind === "cone") {
          const size = 18 * iconScale;
          return (
            <polygon
              key={el.id}
              points={`${x},${y - size} ${x - size * 0.6},${y + size * 0.6} ${
                x + size * 0.6
              },${y + size * 0.6}`}
              fill={el.color || "#ef4444"}
              opacity={0.95}
            />
          );
        }

        if (el.kind === "player") {
          const hasBall = (el as any).hasBall;

          return (
            <g key={el.id}>
              {hasBall && (
                <circle
                  cx={x}
                  cy={y}
                  r={r + 6}
                  fill="none"
                  stroke={el.color || "#facc15"}
                  strokeWidth={3}
                  opacity={0.9}
                />
              )}
              <circle cx={x} cy={y} r={r} fill={el.color || "#2563eb"} opacity={0.98} />
              <text
                x={x}
                y={y + font / 3}
                fontSize={font}
                fill="#fff"
                textAnchor="middle"
                fontWeight={700}
              >
                {el.label ?? ""}
              </text>
            </g>
          );
        }

        if (el.kind === "text") {
          return (
            <text
              key={el.id}
              x={x}
              y={y}
              fontSize={14 * iconScale}
              fill={el.color || "#111827"}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {el.label || "Texte"}
            </text>
          );
        }

        if ((el.kind === "arrow" || el.kind === "pass") && el.x2 != null && el.y2 != null) {
          const nx2 = clamp01(el.x2);
          const ny2 = clamp01(el.y2);
          const x2 = nx2 * width;
          const y2 = ny2 * height;

          const headPts = (fx: number, fy: number, tx: number, ty: number) => {
            const a = Math.atan2(ty - fy, tx - fx);
            const h = 12 * iconScale;
            const hx1 = tx - h * Math.cos(a - Math.PI / 6);
            const hy1 = ty - h * Math.sin(a - Math.PI / 6);
            const hx2 = tx - h * Math.cos(a + Math.PI / 6);
            const hy2 = ty - h * Math.sin(a + Math.PI / 6);
            return `${tx},${ty} ${hx1},${hy1} ${hx2},${hy2}`;
          };

          if (el.kind === "pass") {
            return (
              <g key={el.id}>
                <line
                  x1={x}
                  y1={y}
                  x2={x2}
                  y2={y2}
                  stroke={el.color || "#111827"}
                  strokeWidth={3 * iconScale}
                  strokeDasharray="8 6"
                />
                <polygon points={headPts(x, y, x2, y2)} fill={el.color || "#111827"} />
              </g>
            );
          }

          // arrow
          const hasCurve = el.cpx != null && el.cpy != null;
          const cx = hasCurve ? clamp01(el.cpx!) * width : undefined;
          const cy = hasCurve ? clamp01(el.cpy!) * height : undefined;

          if (hasCurve && cx != null && cy != null) {
            const tx = 2 * (x2 - cx);
            const ty = 2 * (y2 - cy);
            const ax = x2 - tx;
            const ay = y2 - ty;

            return (
              <g key={el.id} fill={el.color || "#111827"} stroke={el.color || "#111827"}>
                <path
                  d={`M ${x} ${y} Q ${cx} ${cy} ${x2} ${y2}`}
                  fill="none"
                  strokeWidth={3 * iconScale}
                />
                <polygon points={headPts(ax, ay, x2, y2)} />
              </g>
            );
          }

          return (
            <g key={el.id} fill={el.color || "#111827"} stroke={el.color || "#111827"}>
              <line x1={x} y1={y} x2={x2} y2={y2} strokeWidth={3 * iconScale} />
              <polygon points={headPts(x, y, x2, y2)} />
            </g>
          );
        }

        return null;
      })}
    </svg>
  );
};

// Mini court pour l’aperçu (même logique que TrainingDetails, mais maxWidth plus petit)
const MiniCourtTile: React.FC<{ drawing: DrawingV2 }> = ({ drawing }) => {
  const safe = {
    ...drawing,
    paths: sanitizePaths(drawing.paths),
    elements: Array.isArray(drawing.elements) ? drawing.elements : [],
  };

  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const MAX_W = 260;
  const HEIGHT_TWEAK = 1.3;
  const FULL_RATIO = 1;

  const W = Math.min(Math.max(0, width), MAX_W);
  const H =
    safe.type === "full"
      ? Math.round(W * FULL_RATIO)
      : Math.round(W * HEIGHT_TWEAK);

  return (
    <div ref={ref} className="relative w-full">
      <div style={{ width: "100%", height: `${H}px`, position: "relative" }}>
        <TrainingDrawingCanvas
          onSave={() => {}}
          initialPaths={safe.paths}
          type={safe.type}
          readOnly
          targetWidth={W}
          targetHeightCss={H}
        />
        <div className="absolute inset-0 pointer-events-none">
          <ElementsOverlayRO
            elements={safe.elements || []}
            width={W}
            height={H}
            iconScale={W / 520}
          />
        </div>
      </div>
    </div>
  );
};

/* -------------------- Component -------------------- */
export const BlockTemplates = () => {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<BlockTemplateRow[]>([]);
  const [isPremium, setIsPremium] = useState<boolean>(false);

  // UI
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<"all" | BlockKind>("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .maybeSingle();
      setIsPremium(!!profile?.is_premium);

      const { data, error } = await supabase
        .from("training_block_templates")
        .select("id,user_id,name,kind,payload,created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (!error && data) setTemplates(data as BlockTemplateRow[]);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      const okKind = kind === "all" ? true : t.kind === kind;
      const hay = `${t.name} ${t.payload?.name ?? ""} ${t.payload?.description ?? ""} ${
        t.payload?.technicalNotes ?? ""
      } ${(t.payload?.objectives ?? []).join(" ")}`
        .toLowerCase();
      const okSearch = !q || hay.includes(q);
      return okKind && okSearch;
    });
  }, [templates, search, kind]);

  const refresh = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from("training_block_templates")
      .select("id,user_id,name,kind,payload,created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (data) setTemplates(data as BlockTemplateRow[]);
  };

  const handleDuplicate = async (tpl: BlockTemplateRow) => {
    const name = `${tpl.name} (copie)`;
    const { error } = await supabase.from("training_block_templates").insert({
      user_id: tpl.user_id,
      name,
      kind: tpl.kind,
      payload: tpl.payload,
    });
    if (error) {
      window.alert("Duplication impossible.");
      return;
    }
    await refresh();
  };

  const handleDelete = async (tpl: BlockTemplateRow) => {
    const ok = window.confirm(`Supprimer le modèle "${tpl.name}" ?`);
    if (!ok) return;
    const { error } = await supabase
      .from("training_block_templates")
      .delete()
      .eq("id", tpl.id);
    if (error) {
      window.alert("Suppression impossible.");
      return;
    }
    setTemplates((prev) => prev.filter((x) => x.id !== tpl.id));
  };

  const handleUseInTraining = (tpl: BlockTemplateRow) => {
    nav(`/plantraining?template_id=${tpl.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          {isPremium && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border
              border-amber-300 bg-amber-50 text-amber-700
              dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200"
            >
              <Sparkles className="w-3 h-3" />
              Premium
            </span>
          )}
        </div>

        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <NotebookPen className="w-6 h-6" />
          Modèles de blocs
        </h1>

        {/* Toolbar */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2
               bg-white/80 border-gray-200 backdrop-blur
               dark:bg-gray-900/80 dark:border-gray-800"
          >
            <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as any)}
              className="border rounded px-3 py-2 text-sm
                 bg-white border-gray-300 text-gray-900
                 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              aria-label="Filtrer par type de bloc"
            >
              <option value="all">Tous les types</option>
              <option value="echauffement">Échauffement</option>
              <option value="exercice">Exercice</option>
              <option value="prepa">Prépa</option>
            </select>

            <div className="ml-2 relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un modèle…"
                className="pl-8 pr-3 py-2 rounded border text-sm
                   bg-white border-gray-300 text-gray-900
                   dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          <Link
            to="/plantraining?from=templates"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg shadow text-sm"
            title="Ouvrir la planification"
          >
            <Library className="w-4 h-4" />
            Ouvrir PlanTraining
          </Link>
        </div>

        {/* Content */}
        {loading ? (
          <p className="text-gray-600 dark:text-gray-300">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
            <p className="text-gray-700 dark:text-gray-300">
              Aucun modèle trouvé. Dans PlanTraining, clique sur <strong>Modèle</strong> pour en
              enregistrer.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((tpl) => {
              const drawings = tpl.payload?.drawings ?? [];
              const drawingsCount = drawings.length;
              const objectivesCount = tpl.payload?.objectives?.length ?? 0;
              const created = tpl.created_at
                ? new Date(tpl.created_at).toLocaleDateString("fr-FR")
                : "";

              const firstRaw = drawings[0];
              const first = firstRaw ? normalizeLegacyDrawing(firstRaw) : null;

              return (
                <div
                  key={tpl.id}
                  className="rounded-2xl border bg-white border-gray-200 p-4 shadow-sm
                      dark:bg-gray-900 dark:border-gray-800"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-lg">{tpl.name}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                        <span
                          className="px-2 py-0.5 rounded-full border
                          border-slate-300 bg-slate-50 text-slate-700
                          dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {KIND_LABEL[tpl.kind]}
                        </span>
                        {tpl.payload?.duration ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border
                            border-emerald-300 bg-emerald-50 text-emerald-700
                            dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200"
                          >
                            <Timer className="w-3 h-3" />
                            {tpl.payload.duration}
                          </span>
                        ) : null}
                        {created && (
                          <span className="text-gray-500 dark:text-gray-400">
                            créé le {created}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDuplicate(tpl)}
                        className="p-2 rounded-lg border hover:bg-gray-50
                          border-gray-200 text-gray-700
                          dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                        title="Dupliquer"
                        aria-label="Dupliquer"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tpl)}
                        className="p-2 rounded-lg border hover:bg-red-50
                          border-gray-200 text-red-600
                          dark:border-gray-700 dark:text-red-400 dark:hover:bg-red-900/20"
                        title="Supprimer"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* mini aperçu du 1er dessin */}
                  {first ? (
                    <div className="mt-3 mb-3">
                      <MiniCourtTile drawing={first} />
                    </div>
                  ) : (
                    <div className="mt-3 mb-3 text-xs text-gray-500 dark:text-gray-400">
                      Pas de dessin
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                    <span className="inline-flex items-center gap-1">
                      <Layers3 className="w-4 h-4" />
                      {drawingsCount} plan(s)
                    </span>
                    <span>{objectivesCount} objectif(s)</span>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={() => handleUseInTraining(tpl)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                        bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Utiliser dans PlanTraining
                    </button>
                    <Link
                      to={`/plandetails?template_id=${tpl.id}`}
                      className="text-sm text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      Aperçu
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
